import { getHashedName, getNameAccountKey, NameRegistryState } from "@bonfida/spl-name-service";
import type { Metadata, Nft, NftWithToken, Sft, SftWithToken } from "@metaplex-foundation/js";
import { Metaplex } from "@metaplex-foundation/js";
import type { Mint } from "@solana/spl-token0.3.8";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import type { MinimalTokenInfo } from "@stepfinance/common";
import { asyncSettlePromises, fromLamports, NATIVE_SOL, SOL_TLD_AUTHORITY } from "@stepfinance/common";
import type { CurveType } from "@stepfinance/liquidity";
import type { NFT } from "@stepfinance/nft";
import { lightGreen } from "@stepfinance/react";
import type { NormalizedTokenAccount, TokenAccount } from "@stepfinance/tokens";
import BN from "bn.js";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";

import { WAD, ZERO } from "../constants";
import type { CommonStrings } from "../contexts/language/languageTypes";
import { formatCurrency as cryptoFormatCurrency } from "../utils/cryptoformat";
import { addTokenDataToMap } from "./tokenInfo";

export const deepClone = (value) => JSON.parse(JSON.stringify(value));

export const capitalizeText = (text: string): string => {
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Accepts a string with "{}" placeholders and returns it filled with the provided values, which can be numbers, strings, or React Elements
 *
 * `formatStringWithPlaceHolders("You {} {} {}", "wrapped", 3, "SOL") -> "You wrapped 3 SOL"`
 */
export const formatStringWithPlaceholders = (str: string, ...values: ReactNode[]): ReactNode[] => {
  const existingPhrases = str.split("{}");
  const res: ReactNode[] = [existingPhrases[0]];
  for (let i = 1; i < existingPhrases.length; i++) {
    res.push(values[i - 1]);
    res.push(existingPhrases[i]);
  }
  return res;
};

export const replaceUrlWithoutNavigation = (newPath: string) => {
  window.history.replaceState(null, "Step", newPath);
};

export const promiseDelay = (ms = 1000) => new Promise((r) => setTimeout(r, ms));

export const getUnixTs = () => {
  return new Date().getTime() / 1000;
};

export const formatPriceNumber = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 8,
});

export function useLocalStorageState(key: string, defaultState?: string | boolean | Array<unknown> | number) {
  const [state, setState] = useState(() => {
    // NOTE: Not sure if this is ok
    const storedState = localStorage.getItem(key);
    if (storedState) {
      return JSON.parse(storedState);
    }
    return defaultState;
  });

  useEffect(() => {
    if (state === null) {
      localStorage.removeItem(key);
    } else if (state !== undefined) {
      localStorage.setItem(key, JSON.stringify(state));
    }
  }, [state, key]);

  return [state, setState];
}

// shorten the checksummed version of the input address to have 4 characters at start and end
export function shortenAddress(address: string, chars = 4): string {
  if (chars === 0) {
    return address;
  }
  return `${address.slice(0, chars)}...${address.slice(-chars)}`;
}

export const STABLE_COINS = new Set(["USDC", "wUSDC", "WUSDC", "USDT", "wUSDT", "WUSDT"]);

export const isFulfilled = <T>(input: PromiseSettledResult<T>): input is PromiseFulfilledResult<T> =>
  input.status === "fulfilled";

export function wadToLamports(amount?: BN): BN {
  return amount?.div(WAD) || ZERO;
}

const SI_SYMBOL = ["", "k", "M", "G", "T", "P", "E"];

const abbreviateNumber = (number: number, precision: number) => {
  const tier = (Math.log10(number) / 3) | 0;
  let scaled = number;
  const suffix = SI_SYMBOL[tier];
  if (tier !== 0) {
    const scale = Math.pow(10, tier * 3);
    scaled = number / scale;
  }

  return scaled.toFixed(precision) + suffix;
};

export const formatAmount = (val: number, precision = 6, abbr = true) =>
  abbr ? abbreviateNumber(val, precision) : val.toFixed(precision);

export function formatTokenAmount(
  account?: TokenAccount,
  mint?: Mint,
  rate = 1.0,
  prefix = "",
  suffix = "",
  precision = 6,
  abbr = false,
): string {
  if (!account) {
    return "";
  }

  return `${[prefix]}${formatAmount(
    fromLamports(new BN(account.info.amount.toString()), mint, rate),
    precision,
    abbr,
  )}${suffix}`;
}

// TODO: Deprecate this function and use formatPercentage()
export const formatPercent = (maximumFractionDigits = 2) =>
  new Intl.NumberFormat("en-US", {
    style: "percent",
    minimumFractionDigits: 2,
    maximumFractionDigits: maximumFractionDigits,
  });

export const formatCurrency = (price: number, currency: string, decimalPlaces = 2) => {
  const isDecimalNumber = Number(price) < 1;
  // i.e. Japanese Yuan doesn't have decimal places
  const isIntegerCurrency = Number(decimalPlaces) === 0;
  // When currency price is above 1, only show two decimals
  const formattedDecimalPlaces = isDecimalNumber ? decimalPlaces : 2;

  return cryptoFormatCurrency(price, currency, "en", false, {
    decimalPlaces: isIntegerCurrency ? 0 : formattedDecimalPlaces,
    minimumDecimalPlaces: null,
  });
};

export const formatUSD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export const numberFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const isSmallNumber = (val: number) => {
  return val < 0.001 && val > 0;
};

export const formatNumber = {
  format: (val?: number, useSmall?: boolean) => {
    if (!val) {
      return "--";
    }
    if (useSmall && isSmallNumber(val)) {
      return 0.001;
    }

    return numberFormatter.format(val);
  },
  formatWithZero: (val: number) => {
    if (!val) {
      return 0;
    }
    return numberFormatter.format(val);
  },
};

export const feeFormatter = new Intl.NumberFormat("en-US", {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 9,
});

export const formatPct = new Intl.NumberFormat("en-US", {
  style: "percent",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const localeDateYearFormatter = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

export const localeTimeFormatter = new Intl.DateTimeFormat(undefined, {
  minute: "2-digit",
  hour: "2-digit",
  timeZoneName: "short",
});

export const formatApyAprPercent = (apy: number) => {
  const apyFormatter = new Intl.NumberFormat("en-US", {
    style: "percent",
    maximumSignificantDigits: 4,
  });
  const apyAprCap = 99.99;
  if (apy > apyAprCap) {
    return ">" + apyFormatter.format(apyAprCap);
  } else if (apy < 0.01) {
    return "<1%";
  } else {
    return apyFormatter.format(apy);
  }
};

export const colorWarning = (value = 0) => {
  const red = "red";
  const orange = "#FF5A00";
  if (value >= 2) {
    return red;
  } else if (value > 1 && value < 2) {
    return orange;
  } else {
    return lightGreen;
  }
};

export const calculatePriceOrBalance = (currency: string, price: number | null, maximumFractionDigits = 2) => {
  const fractionCap = 1 / Math.pow(10, maximumFractionDigits);
  if (price === 0 || price === null) {
    return "--";
  } else if (price < fractionCap) {
    return currency === "USD" ? `< $${fractionCap}` : `< ${fractionCap} ${currency}`;
  }
  return formatCurrency(price, currency, maximumFractionDigits);
};

export const countdownRenderString = ({ days, hours, minutes, seconds }) => {
  const numYears = Math.floor(days / 365);
  const yearString = numYears > 0 ? `${numYears}y ` : "";
  const dayString = days > 0 ? `${days % 365}d ` : "";
  const hoursString = hours > 0 ? `${hours}h ` : "";
  const minutesString = minutes > 0 ? `${minutes}m` : "";
  // if no days hours or minutes, show seconds;
  const secondsString = days === 0 && hours === 0 && minutes === 0 ? `${seconds}s` : "";
  return yearString + dayString + hoursString + minutesString + secondsString;
};

export const daysToYearsAndRemainingDayStr = (durationDays: number, common: CommonStrings) => {
  const yearStr = durationDays >= 365 ? Math.floor(durationDays / 365) + " " + common.timeSpans.years + " " : "";
  const dayStr = durationDays % 365 ? (durationDays % 365) + " " + common.timeSpans.days : "";
  const totalDurationStr = yearStr + dayStr;
  return { yearStr, dayStr, totalDurationStr };
};

type DomainRegistryInfo = {
  inputDomainKey: PublicKey;
  hashedInputName: Buffer;
};

const getDomainInputKey = async (inputStr: string): Promise<DomainRegistryInfo> => {
  const hashedInputName: Buffer = await getHashedName(inputStr);
  const inputDomainKey: PublicKey = await getNameAccountKey(hashedInputName, undefined, SOL_TLD_AUTHORITY);
  return {
    inputDomainKey: inputDomainKey,
    hashedInputName: hashedInputName,
  };
};

export const getAccountFromDomainRegistry = async (inputStr: string, connection: Connection): Promise<PublicKey> => {
  const { inputDomainKey } = await getDomainInputKey(inputStr.toLowerCase().replace(".sol", ""));
  const registry = await NameRegistryState.retrieve(connection, inputDomainKey);
  return registry.owner;
};

export const validateAddressInput = async (input: string, connection: Connection, nativeAccount = false) => {
  let address: PublicKey;
  try {
    if (input.toLowerCase().endsWith(".sol")) {
      address = await getAccountFromDomainRegistry(input, connection);
    } else {
      address = new PublicKey(input);
      const destinationAccountInfo = await connection.getAccountInfo(address);
      if (nativeAccount) {
        if (destinationAccountInfo !== null && !destinationAccountInfo.owner.equals(NATIVE_SOL)) {
          throw new Error("Not valid Solana Address");
        }
      }
    }
  } catch (err) {
    return null;
  }
  return address;
};

type CurveTypeFields = `${CurveType}`;
type CurveTypeNameMap = {
  [curveType in CurveTypeFields]: string;
};
const CURVE_TYPE_NAME_MAP: CurveTypeNameMap = {
  0: "Constant Product",
  1: "Constant Price",
  2: "Constant Product w/ Offset",
  3: "Stable",
  4: "Concentrated Liquidity",
};
export const getCurveTypeName = (curveType: CurveType) => CURVE_TYPE_NAME_MAP[curveType];

export const fetchJSONFromUrl = async <T>(url: RequestInfo | URL, options?: RequestInit): Promise<T> => {
  const req = await fetch(url, options);
  if (!req.ok) {
    throw new Error("Failed to fetch");
  }
  return await req.json();
};

export const fetchWithRetry = (url: RequestInfo, options: RequestInit = {}, retries = 3) =>
  fetch(url, options).then((res) => {
    if (res.ok) {
      return res.json();
    }
    if (retries > 0) {
      return fetchWithRetry(url, options, retries - 1);
    }
    throw new Error(`Retry attempts failed for request to ${url}`);
  });

export const isPaginationHidden = (listLength: number, pageSizes: string[]): boolean => {
  return listLength <= Number(pageSizes[0]);
};

export const isFungibleToken = (token: NormalizedTokenAccount | NFT): token is NormalizedTokenAccount => {
  return (token as NormalizedTokenAccount).info !== undefined;
};

export const fetchAndCacheMetaplexMetadata = async (
  connection: Connection,
  mints: PublicKey[],
  filterNFTs: boolean,
) => {
  const nftClient = new Metaplex(connection).nfts();
  const response = await nftClient.findAllByMintList({ mints });
  const tokenDataFetches: Promise<Nft | Sft | SftWithToken | NftWithToken>[] = [];
  for (let i = 0; i < response.length; i++) {
    const metadata = response[i];
    if (!metadata) {
      continue;
    }
    // check for token standard 2 as that is fungible token standard number, ignore NFTs
    if (filterNFTs && metadata.tokenStandard !== 2) {
      continue;
    }
    tokenDataFetches.push(nftClient.load({ metadata: metadata as Metadata }));
  }
  const tokenDataResults = await asyncSettlePromises(tokenDataFetches);
  for (const tokenData of tokenDataResults) {
    if (!tokenData) {
      continue;
    }
    const minimalTokenInfo: MinimalTokenInfo = {
      address: tokenData.address.toBase58(),
      decimals: tokenData.mint.decimals,
      symbol: tokenData.tokenStandard === 2 ? tokenData.mint.currency.symbol : "",
      name: tokenData.json?.name ?? "",
      chainId: 101,
      logoURI: tokenData.json?.image,
    };
    addTokenDataToMap(minimalTokenInfo);
  }
};
/**
 * @param array Data to slice from.
 * @param pageSize Items per page.
 * @param current Current page out of all pages. Defaults to one.
 * @param numPagesToSlice Total pages to slice. defaults to 1.
 * @returns Slice of the selected pages of data.
 */
export const slicePages = <T>(array: T[], pageSize: number, current = 1, numPagesToSlice = 1) => {
  const start = (current - 1) * pageSize;
  const end = current * pageSize + (numPagesToSlice - 1) * pageSize;
  return array.slice(start, end);
};

export const createRange = (start: number, end: number) => {
  const list: number[] = [];
  const isDescending = start > end;
  if (isDescending) {
    for (let i = start; i >= end; i--) {
      list.push(i);
    }
  } else {
    for (let i = start; i <= end; i++) {
      list.push(i);
    }
  }
  return list;
};

export const downloadFile = ({ data, fileName, fileType }) => {
  // Create a blob with the data we want to download as a file
  const blob = new Blob([data], { type: fileType });
  // Create an anchor element and dispatch a click event on it
  // to trigger a download
  const a = document.createElement("a");
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  const clickEvt = new MouseEvent("click", {
    view: window,
    bubbles: true,
    cancelable: true,
  });
  a.dispatchEvent(clickEvt);
  a.remove();
};
